/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React from "react";

import { PurchaserQuestionnaireQuestionHeader } from "./PurchaserQuestionnaireQuestionHeader";
import {
  LOAD_MORE_LIMIT as answersPerPage,
  useGetTicketQuestionTextAnswers,
} from "./useGetTicketQuestionTextAnswers";

import type { FreeTextQuestionData } from "./purchaserQuestionnaireData";

type FreeTextQuestionCardProps = {
  eventIds: string[];
  eventName?: string;
  question: FreeTextQuestionData;
};

type FreeTextAnswerDetail = FreeTextQuestionData["answerDetails"][number];
type FreeTextAnswerTicket = FreeTextAnswerDetail["tickets"][number];

export const FreeTextQuestionCard: React.FC<FreeTextQuestionCardProps> = ({
  eventIds,
  eventName,
  question,
}) => {
  const [visibleAnswerCount, setVisibleAnswerCount] =
    React.useState(answersPerPage);

  // SWRのフェッチをユーザーアクションまで遅延させるためのフラグ
  const [isFetchingEnabled, setIsFetchingEnabled] = React.useState(false);

  // 初期取得したデータの先頭（一番最新の回答日時）をスナップショットとして固定する
  const snapshotAt = React.useMemo(
    () => question.answerDetails[0]?.lastAnsweredAt ?? undefined,
    [question.answerDetails]
  );

  // フックを呼び出す（isFetchingEnabledがfalseの間はeventIdsを空にして取得を一時停止する）
  const {
    data: loadMoreAnswers,
    isLoading,
    size,
    setSize,
    error,
    mutate,
  } = useGetTicketQuestionTextAnswers(
    isFetchingEnabled ? eventIds : [],
    question.questionId,
    snapshotAt
  );

  // 初期ロードの回答と、もっと見るで追加取得した回答を結合する
  const allLoadedAnswers = React.useMemo(() => {
    const baseAnswers = question.answerDetails;
    const extraAnswers = loadMoreAnswers.map((ans, i) => ({
      ...ans,
      answerLabel: `#${baseAnswers.length + i + 1}`,
    })) as unknown as FreeTextAnswerDetail[];

    return [...baseAnswers, ...extraAnswers];
  }, [question.answerDetails, loadMoreAnswers]);

  const totalAnswerCount = question.totalCount ?? question.answerDetails.length;
  const normalizedVisibleAnswerCount = Math.min(
    visibleAnswerCount,
    totalAnswerCount,
    allLoadedAnswers.length
  );
  const visibleAnswers = allLoadedAnswers.slice(
    0,
    normalizedVisibleAnswerCount
  );

  // 残りの件数は「表示できている実際の件数」を基準に計算する
  const remainingAnswerCount = totalAnswerCount - normalizedVisibleAnswerCount;
  const nextAnswerCount = Math.min(answersPerPage, remainingAnswerCount);
  const hasMoreAnswers = remainingAnswerCount > 0;
  const canCollapseAnswers = normalizedVisibleAnswerCount > answersPerPage;
  const answerListId = `purchaser-questionnaire-${question.questionLabel.toLowerCase()}-answers`;

  const handleShowMore = () => {
    if (error) {
      if (!isFetchingEnabled) {
        setIsFetchingEnabled(true);
      } else {
        void mutate();
      }
      return;
    }

    const nextCount = Math.min(
      visibleAnswerCount + answersPerPage,
      totalAnswerCount
    );
    setVisibleAnswerCount(nextCount);

    // メモリ上のデータ（現在ロード済みの件数）が表示したい件数に足りなくなった場合のみ、APIを追加で叩く
    if (nextCount > allLoadedAnswers.length) {
      if (!isFetchingEnabled) {
        setIsFetchingEnabled(true);
      } else {
        void setSize(size + 1);
      }
    }
  };

  const handleCollapse = () => {
    setVisibleAnswerCount(answersPerPage);
  };

  return (
    <article css={styles.card}>
      <PurchaserQuestionnaireQuestionHeader
        eventName={eventName}
        metadata={<span>回答 {formatCount(totalAnswerCount)}件</span>}
        questionLabel={question.questionLabel}
        text={question.text}
      />

      <ol
        css={styles.answerList}
        id={answerListId}
        aria-label={`${question.questionLabel} 自由記述回答`}
      >
        {visibleAnswers.map((answerDetail) => (
          <li css={styles.answerListItem} key={answerDetail.answerLabel}>
            <article css={styles.answerCard}>
              <div css={styles.answerMetadata}>
                <FreeTextAnswerTickets tickets={answerDetail.tickets} />
                <span css={styles.purchaserAttributes}>
                  {formatAgeGroup(answerDetail.ageGroup)}{" "}
                  {formatGender(answerDetail.gender)}
                </span>
                <span css={styles.answerLabel}>{answerDetail.answerLabel}</span>
              </div>
              <p css={styles.answerText}>{answerDetail.answer}</p>
            </article>
          </li>
        ))}
      </ol>

      <div css={styles.pagination}>
        <span css={styles.paginationStatus} aria-live="polite">
          {formatCount(normalizedVisibleAnswerCount)} /{" "}
          {formatCount(totalAnswerCount)}件を表示
        </span>
        {hasMoreAnswers || canCollapseAnswers ? (
          <div css={styles.paginationActions}>
            {hasMoreAnswers ? (
              <button
                css={[
                  styles.paginationButton,
                  error ? styles.errorButton : styles.showMoreButton,
                  isLoading && styles.loadingButton,
                ]}
                type="button"
                aria-controls={answerListId}
                onClick={handleShowMore}
                disabled={isLoading}
              >
                {error
                  ? "読み込みに失敗しました（再試行）"
                  : isLoading
                    ? "読み込み中..."
                    : `さらに${formatCount(nextAnswerCount)}件を表示`}
              </button>
            ) : null}
            {canCollapseAnswers ? (
              <button
                css={[styles.paginationButton, styles.collapseButton]}
                type="button"
                aria-controls={answerListId}
                onClick={handleCollapse}
                disabled={isLoading}
              >
                折りたたむ
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );
};

const FreeTextAnswerTickets: React.FC<{
  tickets: FreeTextAnswerDetail["tickets"];
}> = ({ tickets }) => {
  const [visibleTicket] = tickets;
  if (visibleTicket === undefined) {
    return null;
  }

  const hiddenTicketCount = tickets.length - 1;
  const allTicketNamesText = tickets
    .map(({ ticketName }) => ticketName)
    .join(", ");

  return (
    <ul css={styles.ticketList}>
      <li css={styles.ticketTag(visibleTicket.productType)}>
        {visibleTicket.ticketName}
      </li>
      {hiddenTicketCount > 0 ? (
        <li
          css={styles.hiddenTickets}
          tabIndex={0}
          aria-label={`すべてのチケット名: ${allTicketNamesText}`}
        >
          +{hiddenTicketCount}
          <ul css={styles.ticketNamesTooltip} aria-hidden="true">
            {tickets.map((ticket) => (
              <li
                css={styles.ticketNameTooltipItem}
                key={`${ticket.eventId}-${ticket.ticketId}`}
              >
                {ticket.ticketName}
              </li>
            ))}
          </ul>
        </li>
      ) : null}
    </ul>
  );
};

const formatCount = (count: number): string => count.toLocaleString("ja-JP");

const ageGroupLabels: Record<FreeTextAnswerDetail["ageGroup"], string> = {
  UNDER_10: "10歳未満",
  FROM_10_TO_19: "10代",
  FROM_20_TO_29: "20代",
  FROM_30_TO_39: "30代",
  FROM_40_TO_49: "40代",
  FROM_50_TO_59: "50代",
  FROM_60_TO_69: "60代",
  OVER_70: "70代以上",
  NO_ANSWER: "年齢未回答",
};

const genderLabels: Record<FreeTextAnswerDetail["gender"], string> = {
  MALE: "男性",
  FEMALE: "女性",
  OTHER: "その他",
  NO_ANSWER: "性別未回答",
};

const formatAgeGroup = (ageGroup: FreeTextAnswerDetail["ageGroup"]): string =>
  ageGroupLabels[ageGroup];

const formatGender = (gender: FreeTextAnswerDetail["gender"]): string =>
  genderLabels[gender];

const styles = {
  card: css`
    min-width: 0;
    padding: clamp(16px, 2.5vw, 24px);
    background: #1b2734;
    border: 1px solid #2e3d4c;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgb(0 0 0 / 22%);
  `,
  answerList: css`
    display: grid;
    grid-template-columns: repeat(
      auto-fit,
      minmax(min(100%, calc(340 / 16 * 1rem)), 1fr)
    );
    gap: 14px;
    padding: 0;
    margin: 16px 0 0;
    list-style: none;
  `,
  answerListItem: css`
    min-width: 0;
  `,
  answerCard: css`
    height: 100%;
    padding: 14px 15px;
    background: #22313f;
    border: 1px solid #2e3d4c;
    border-radius: 9px;
  `,
  answerMetadata: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
    margin-bottom: 9px;
  `,
  ticketList: css`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
    padding: 0;
    margin: 0;
    list-style: none;
  `,
  ticketTag: (productType: FreeTextAnswerTicket["productType"]) => css`
    max-width: 100%;
    padding: 2px 9px;
    color: ${productType === "STREAMING_TICKET" ? "#00c2ae" : "#d6a45f"};
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.3px;
    line-height: 1.5;
    background: ${productType === "STREAMING_TICKET"
      ? "rgb(0 194 174 / 15%)"
      : "rgb(214 164 95 / 16%)"};
    border-radius: 20px;
    overflow-wrap: anywhere;
  `,
  hiddenTickets: css`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    padding: 2px 7px;
    color: #b9c3cd;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.3px;
    line-height: 1.5;
    background: rgb(139 150 162 / 14%);
    border: 1px solid rgb(139 150 162 / 35%);
    border-radius: 20px;
    cursor: default;
    &:hover {
      color: #ffffff;
      background: rgb(139 150 162 / 24%);
      border-color: rgb(139 150 162 / 65%);
    }
    &:hover > ul,
    &:focus-visible > ul {
      visibility: visible;
      opacity: 1;
    }
    &:focus-visible {
      outline: 2px solid #00c2ae;
      outline-offset: 2px;
    }
  `,
  ticketNamesTooltip: css`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: max-content;
    max-width: min(calc(280 / 16 * 1rem), calc(100vw - 48px));
    max-height: 240px;
    padding: 10px 12px;
    margin: 0;
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 1.5;
    white-space: normal;
    overflow-y: auto;
    background: #111f2d;
    border: 1px solid #2f4052;
    border-radius: 6px;
    box-shadow: 0 10px 24px rgb(0 0 0 / 32%);
    visibility: hidden;
    opacity: 0;
    z-index: 2;
  `,
  ticketNameTooltipItem: css`
    list-style-position: inside;
    overflow-wrap: anywhere;
  `,
  purchaserAttributes: css`
    color: #8b96a2;
    font-size: 11px;
    letter-spacing: 0.3px;
    line-height: 1.5;
  `,
  answerLabel: css`
    margin-left: auto;
    color: #8b96a2;
    font-size: 10.5px;
    font-variant-numeric: tabular-nums;
    opacity: 0.7;
  `,
  answerText: css`
    margin: 0;
    color: #b9c3cd;
    font-size: 13px;
    line-height: 1.65;
    overflow-wrap: anywhere;
  `,
  pagination: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    margin-top: 14px;
  `,
  paginationStatus: css`
    color: #8b96a2;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
  `,
  paginationActions: css`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
  `,
  paginationButton: css`
    min-height: 44px;
    padding: 11px 22px;
    font: inherit;
    font-size: 13.5px;
    font-weight: 700;
    letter-spacing: 0.4px;
    border-radius: 8px;
    cursor: pointer;
    &:focus-visible {
      outline: 2px solid #00c2ae;
      outline-offset: 2px;
    }
  `,
  showMoreButton: css`
    padding-right: 26px;
    padding-left: 26px;
    color: #ffffff;
    background: #00c2ae;
    border: 1px solid #00c2ae;
    &:hover {
      background: #33d6c4;
      border-color: #33d6c4;
    }
  `,
  errorButton: css`
    padding-right: 26px;
    padding-left: 26px;
    color: #ffffff;
    background: #e74c3c;
    border: 1px solid #e74c3c;
    &:hover {
      background: #c0392b;
      border-color: #c0392b;
    }
  `,
  loadingButton: css`
    opacity: 0.7;
    cursor: not-allowed;
    &:hover {
      background: #00c2ae;
      border-color: #00c2ae;
    }
  `,
  collapseButton: css`
    color: #b9c3cd;
    background: #22313f;
    border: 1px solid #2e3d4c;
    &:hover {
      color: #ffffff;
      border-color: #00c2ae;
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `,
};
